/**
 * useSubstrateResources Hook
 *
 * Provides data fetching for substrate-related reference data:
 * - Recipes (substrate_recipes + recipe_ingredients)
 * - Block formats (block_formats)
 *
 * Part of US-P2-003: Substrate Production & Block Creation
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'

/**
 * Hook to fetch substrate recipes with ingredients
 * @param {Object} options - Filter options
 * @param {boolean} options.activeOnly - Only fetch active recipes (default: true)
 * @returns {Object} { recipes, loading, error, refetch }
 */
export function useRecipes(options = {}) {
  const [recipes, setRecipes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const activeOnly = options.activeOnly !== false

  const fetchRecipes = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      let query = supabase
        .from('substrate_recipes')
        .select(`
          id,
          name,
          compatible_species,
          mixer_capacity_kg,
          blocks_per_batch,
          version_major,
          version_minor,
          active,
          notes,
          created_at,
          updated_at,
          recipe_ingredients (
            id,
            material_name,
            parts,
            quantity_kg,
            notes,
            item_id,
            track_inventory
          )
        `)
        .order('name')

      if (activeOnly) {
        query = query.eq('active', true)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        throw new Error(fetchError.message)
      }

      // Transform to include ingredients as a nested array
      const transformedRecipes = (data || []).map(recipe => ({
        ...recipe,
        ingredients: recipe.recipe_ingredients || []
      }))

      setRecipes(transformedRecipes)
    } catch (err) {
      console.error('[useRecipes] Error:', err)
      setError(err.message)
      setRecipes([])
    } finally {
      setLoading(false)
    }
  }, [activeOnly])

  useEffect(() => {
    fetchRecipes()
  }, [fetchRecipes])

  return {
    recipes,
    loading,
    error,
    refetch: fetchRecipes
  }
}

/**
 * Hook to fetch a single recipe with ingredients
 * @param {string} recipeId - Recipe UUID
 * @returns {Object} { recipe, ingredients, loading, error, refetch }
 */
export function useRecipe(recipeId) {
  const [recipe, setRecipe] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchRecipe = useCallback(async () => {
    if (!recipeId) {
      setRecipe(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('substrate_recipes')
        .select(`
          id,
          name,
          compatible_species,
          mixer_capacity_kg,
          blocks_per_batch,
          version_major,
          version_minor,
          active,
          notes,
          created_at,
          updated_at,
          recipe_ingredients (
            id,
            material_name,
            parts,
            quantity_kg,
            notes,
            item_id,
            track_inventory
          )
        `)
        .eq('id', recipeId)
        .single()

      if (fetchError) {
        throw new Error(fetchError.message)
      }

      setRecipe({
        ...data,
        ingredients: data.recipe_ingredients || []
      })
    } catch (err) {
      console.error('[useRecipe] Error:', err)
      setError(err.message)
      setRecipe(null)
    } finally {
      setLoading(false)
    }
  }, [recipeId])

  useEffect(() => {
    fetchRecipe()
  }, [fetchRecipe])

  return {
    recipe,
    ingredients: recipe?.ingredients || [],
    loading,
    error,
    refetch: fetchRecipe
  }
}

/**
 * Hook for recipe mutations (create, update, delete)
 * @returns {Object} Mutation functions and state
 */
export function useRecipeMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Create a new recipe with ingredients
   * @param {Object} data - Recipe data
   * @param {string} data.name - Recipe name
   * @param {string[]} data.compatible_species - Array of compatible species
   * @param {number} data.mixer_capacity_kg - Mixer capacity
   * @param {number} data.blocks_per_batch - Blocks per batch
   * @param {number} data.version_major - Major version number
   * @param {number} data.version_minor - Minor version number (0 = main, 1+ = experimental)
   * @param {string} data.notes - Optional notes
   * @param {Array} data.ingredients - Array of { material_name, parts }
   * @returns {Promise<Object>} Created recipe
   */
  const createRecipe = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      // Create recipe
      const { data: recipe, error: recipeError } = await supabase
        .from('substrate_recipes')
        .insert({
          name: data.name,
          compatible_species: data.compatible_species || [],
          mixer_capacity_kg: data.mixer_capacity_kg || 243,
          blocks_per_batch: data.blocks_per_batch || 54,
          version_major: data.version_major || 1,
          version_minor: data.version_minor || 0,
          notes: data.notes,
          active: true
        })
        .select()
        .single()

      if (recipeError) {
        throw new Error(recipeError.message)
      }

      // Create ingredients with parts (ratio-based)
      if (data.ingredients && data.ingredients.length > 0) {
        const ingredients = data.ingredients.map(ing => ({
          recipe_id: recipe.id,
          material_name: ing.material_name,
          parts: ing.parts,
          // Also set quantity_kg for backward compatibility (same as parts during migration)
          quantity_kg: ing.parts,
          notes: ing.notes
        }))

        const { error: ingError } = await supabase
          .from('recipe_ingredients')
          .insert(ingredients)

        if (ingError) {
          throw new Error(`Failed to create ingredients: ${ingError.message}`)
        }
      }

      return recipe
    } catch (err) {
      console.error('[useRecipeMutations.createRecipe] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Update an existing recipe
   * @param {Object} data - Update data
   * @param {string} data.id - Recipe UUID
   * @param {string} data.name - New name
   * @param {string[]} data.compatible_species - New species array
   * @param {number} data.mixer_capacity_kg - New mixer capacity
   * @param {number} data.blocks_per_batch - New blocks per batch
   * @param {number} data.version_major - Major version number
   * @param {number} data.version_minor - Minor version number (0 = main, 1+ = experimental)
   * @param {boolean} data.active - Active status
   * @param {string} data.notes - Notes
   * @param {Array} data.ingredients - New ingredients (replaces existing)
   * @returns {Promise<Object>} Updated recipe
   */
  const updateRecipe = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      const updateData = {}
      if (data.name !== undefined) updateData.name = data.name
      if (data.compatible_species !== undefined) updateData.compatible_species = data.compatible_species
      if (data.mixer_capacity_kg !== undefined) updateData.mixer_capacity_kg = data.mixer_capacity_kg
      if (data.blocks_per_batch !== undefined) updateData.blocks_per_batch = data.blocks_per_batch
      if (data.version_major !== undefined) updateData.version_major = data.version_major
      if (data.version_minor !== undefined) updateData.version_minor = data.version_minor
      if (data.active !== undefined) updateData.active = data.active
      if (data.notes !== undefined) updateData.notes = data.notes

      const { data: recipe, error: recipeError } = await supabase
        .from('substrate_recipes')
        .update(updateData)
        .eq('id', data.id)
        .select()
        .single()

      if (recipeError) {
        throw new Error(recipeError.message)
      }

      // Replace ingredients if provided
      if (data.ingredients !== undefined) {
        // Delete existing ingredients
        await supabase
          .from('recipe_ingredients')
          .delete()
          .eq('recipe_id', data.id)

        // Insert new ingredients with parts (ratio-based)
        if (data.ingredients.length > 0) {
          const ingredients = data.ingredients.map(ing => ({
            recipe_id: data.id,
            material_name: ing.material_name,
            parts: ing.parts,
            // Also set quantity_kg for backward compatibility
            quantity_kg: ing.parts,
            notes: ing.notes
          }))

          const { error: ingError } = await supabase
            .from('recipe_ingredients')
            .insert(ingredients)

          if (ingError) {
            throw new Error(`Failed to update ingredients: ${ingError.message}`)
          }
        }
      }

      return recipe
    } catch (err) {
      console.error('[useRecipeMutations.updateRecipe] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Delete a recipe (soft delete by setting active = false)
   * @param {string} id - Recipe UUID
   * @returns {Promise<void>}
   */
  const deleteRecipe = useCallback(async (id) => {
    setLoading(true)
    setError(null)

    try {
      // Soft delete
      const { error: deleteError } = await supabase
        .from('substrate_recipes')
        .update({ active: false })
        .eq('id', id)

      if (deleteError) {
        throw new Error(deleteError.message)
      }
    } catch (err) {
      console.error('[useRecipeMutations.deleteRecipe] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    createRecipe,
    updateRecipe,
    deleteRecipe
  }
}

/**
 * Hook to fetch block formats with stock from unified inventory system
 * @param {Object} options - Filter options
 * @param {boolean} options.activeOnly - Only fetch active formats (default: true)
 * @returns {Object} { blockFormats, loading, error, refetch }
 *
 * US-P6-002: Inventory Unification
 * Stock is now fetched from inventory_stock_levels view via inventory_item_id
 */
export function useBlockFormats(options = {}) {
  const [blockFormats, setBlockFormats] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const activeOnly = options.activeOnly !== false

  const fetchBlockFormats = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch block formats with linked inventory item IDs
      let query = supabase
        .from('block_formats')
        .select('*')
        .order('name')

      if (activeOnly) {
        query = query.eq('active', true)
      }

      const { data: formats, error: fetchError } = await query

      if (fetchError) {
        throw new Error(fetchError.message)
      }

      // Fetch current stock from inventory_stock_levels view for linked items
      const inventoryItemIds = (formats || [])
        .filter(f => f.inventory_item_id)
        .map(f => f.inventory_item_id)

      let stockLevels = {}
      if (inventoryItemIds.length > 0) {
        const { data: stockData, error: stockError } = await supabase
          .from('inventory_stock_levels')
          .select('item_id, current_stock, reorder_point')
          .in('item_id', inventoryItemIds)

        if (!stockError && stockData) {
          stockLevels = stockData.reduce((acc, item) => {
            acc[item.item_id] = {
              current_stock: item.current_stock || 0,
              reorder_point: item.reorder_point || 0
            }
            return acc
          }, {})
        }
      }

      // Merge stock data into block formats
      const formatsWithStock = (formats || []).map(format => {
        if (format.inventory_item_id && stockLevels[format.inventory_item_id]) {
          const stock = stockLevels[format.inventory_item_id]
          return {
            ...format,
            // Override with unified inventory stock
            current_inventory: stock.current_stock,
            // Use inventory reorder point if available, fallback to min_stock_level
            effective_min_stock: stock.reorder_point || format.min_stock_level || 0
          }
        }
        // Fallback to legacy current_inventory for unlinked formats
        return {
          ...format,
          effective_min_stock: format.min_stock_level || 0
        }
      })

      setBlockFormats(formatsWithStock)
    } catch (err) {
      console.error('[useBlockFormats] Error:', err)
      setError(err.message)
      setBlockFormats([])
    } finally {
      setLoading(false)
    }
  }, [activeOnly])

  useEffect(() => {
    fetchBlockFormats()
  }, [fetchBlockFormats])

  return {
    blockFormats,
    loading,
    error,
    refetch: fetchBlockFormats
  }
}

/**
 * Hook for block format mutations (create, update, delete)
 * @returns {Object} Mutation functions and state
 */
export function useBlockFormatMutations() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Create a new block format
   * @param {Object} data - Block format data
   * @param {string} data.name - Format name
   * @param {string} data.supplier - Supplier name
   * @param {number} data.weight_kg - Weight per block
   * @param {number} data.cost_per_unit - Cost per unit
   * @param {number} data.current_inventory - Current stock
   * @param {number} data.min_stock_level - Minimum stock level for alerts
   * @returns {Promise<Object>} Created block format
   */
  const createBlockFormat = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      const { data: format, error: formatError } = await supabase
        .from('block_formats')
        .insert({
          name: data.name,
          supplier: data.supplier,
          weight_kg: data.weight_kg,
          cost_per_unit: data.cost_per_unit,
          current_inventory: data.current_inventory || 0,
          min_stock_level: data.min_stock_level,
          active: true
        })
        .select()
        .single()

      if (formatError) {
        throw new Error(formatError.message)
      }

      return format
    } catch (err) {
      console.error('[useBlockFormatMutations.createBlockFormat] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Update an existing block format
   * @param {Object} data - Update data
   * @param {string} data.id - Block format UUID
   * @returns {Promise<Object>} Updated block format
   */
  const updateBlockFormat = useCallback(async (data) => {
    setLoading(true)
    setError(null)

    try {
      const updateData = {}
      if (data.name !== undefined) updateData.name = data.name
      if (data.supplier !== undefined) updateData.supplier = data.supplier
      if (data.weight_kg !== undefined) updateData.weight_kg = data.weight_kg
      if (data.cost_per_unit !== undefined) updateData.cost_per_unit = data.cost_per_unit
      if (data.current_inventory !== undefined) updateData.current_inventory = data.current_inventory
      if (data.min_stock_level !== undefined) updateData.min_stock_level = data.min_stock_level
      if (data.active !== undefined) updateData.active = data.active

      const { data: format, error: formatError } = await supabase
        .from('block_formats')
        .update(updateData)
        .eq('id', data.id)
        .select()
        .single()

      if (formatError) {
        throw new Error(formatError.message)
      }

      return format
    } catch (err) {
      console.error('[useBlockFormatMutations.updateBlockFormat] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Delete a block format (soft delete)
   * @param {string} id - Block format UUID
   * @returns {Promise<void>}
   */
  const deleteBlockFormat = useCallback(async (id) => {
    setLoading(true)
    setError(null)

    try {
      const { error: deleteError } = await supabase
        .from('block_formats')
        .update({ active: false })
        .eq('id', id)

      if (deleteError) {
        throw new Error(deleteError.message)
      }
    } catch (err) {
      console.error('[useBlockFormatMutations.deleteBlockFormat] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Adjust inventory for a block format
   * @param {string} id - Block format UUID
   * @param {number} adjustment - Positive or negative adjustment
   * @returns {Promise<Object>} Updated block format
   */
  const adjustInventory = useCallback(async (id, adjustment) => {
    setLoading(true)
    setError(null)

    try {
      // Get current inventory
      const { data: current, error: fetchError } = await supabase
        .from('block_formats')
        .select('current_inventory')
        .eq('id', id)
        .single()

      if (fetchError) {
        throw new Error(fetchError.message)
      }

      const newInventory = Math.max(0, (current.current_inventory || 0) + adjustment)

      const { data: format, error: updateError } = await supabase
        .from('block_formats')
        .update({ current_inventory: newInventory })
        .eq('id', id)
        .select()
        .single()

      if (updateError) {
        throw new Error(updateError.message)
      }

      return format
    } catch (err) {
      console.error('[useBlockFormatMutations.adjustInventory] Error:', err)
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    createBlockFormat,
    updateBlockFormat,
    deleteBlockFormat,
    adjustInventory
  }
}

/**
 * Recipe versioning helper functions
 */

/**
 * Check if a recipe is experimental (version_minor > 0)
 * @param {Object} recipe - Recipe object
 * @returns {boolean} True if experimental
 */
export const isExperimental = (recipe) => recipe.version_minor > 0

/**
 * Format version string for display
 * @param {Object} recipe - Recipe object
 * @returns {string} Formatted version (e.g., "v1" or "v1.1")
 */
export const formatVersion = (recipe) =>
  recipe.version_minor > 0
    ? `v${recipe.version_major}.${recipe.version_minor}`
    : `v${recipe.version_major}`

/**
 * Calculate next experimental version for a parent recipe
 * @param {Object} parentRecipe - The main recipe (version_minor = 0)
 * @param {Array} existingExperimentals - Array of existing experimental recipes
 * @returns {Object} { version_major, version_minor } for next experimental
 */
export const getNextExperimentalVersion = (parentRecipe, existingExperimentals) => {
  const sameParentExperimentals = existingExperimentals.filter(
    r => r.version_major === parentRecipe.version_major
  )
  const maxMinor = Math.max(0, ...sameParentExperimentals.map(r => r.version_minor))
  return { version_major: parentRecipe.version_major, version_minor: maxMinor + 1 }
}

/**
 * Promote an experimental recipe to become the new main version
 * @param {string} experimentalRecipeId - ID of the experimental recipe to promote
 * @returns {Promise<Object>} The new main recipe with ingredients
 * @throws {Error} If recipe not found, not experimental, or promotion fails
 */
export const promoteToMain = async (experimentalRecipeId) => {
  try {
    // 1. Get the experimental recipe with its ingredients
    const { data: experimental, error: fetchError } = await supabase
      .from('substrate_recipes')
      .select(`
        id,
        name,
        compatible_species,
        mixer_capacity_kg,
        blocks_per_batch,
        version_major,
        version_minor,
        parent_recipe_id,
        active,
        notes,
        recipe_ingredients (
          id,
          material_name,
          parts,
          quantity_kg,
          notes,
          item_id,
          track_inventory
        )
      `)
      .eq('id', experimentalRecipeId)
      .single()

    if (fetchError) {
      throw new Error(`Failed to fetch experimental recipe: ${fetchError.message}`)
    }

    // Validate it's an experimental recipe
    if (experimental.version_minor === 0) {
      throw new Error('Cannot promote main recipe - only experimental recipes (version_minor > 0) can be promoted')
    }

    // 2. Find the parent (main recipe) via parent_recipe_id
    if (!experimental.parent_recipe_id) {
      throw new Error('Experimental recipe missing parent_recipe_id - cannot determine lineage')
    }

    const { data: parentRecipe, error: parentError } = await supabase
      .from('substrate_recipes')
      .select('id, version_major, is_current')
      .eq('id', experimental.parent_recipe_id)
      .single()

    if (parentError) {
      throw new Error(`Failed to fetch parent recipe: ${parentError.message}`)
    }

    // 3. Create new main version
    const newMainRecipeData = {
      name: experimental.name,
      compatible_species: experimental.compatible_species,
      mixer_capacity_kg: experimental.mixer_capacity_kg,
      blocks_per_batch: experimental.blocks_per_batch,
      version_major: parentRecipe.version_major + 1,
      version_minor: 0,
      parent_recipe_id: experimentalRecipeId, // Track history: new main came from this experimental
      is_current: true,
      active: true,
      notes: experimental.notes
    }

    const { data: newMainRecipe, error: createError } = await supabase
      .from('substrate_recipes')
      .insert(newMainRecipeData)
      .select()
      .single()

    if (createError) {
      throw new Error(`Failed to create new main recipe: ${createError.message}`)
    }

    // 4. Mark old main recipe as is_current = false
    const { error: updateParentError } = await supabase
      .from('substrate_recipes')
      .update({ is_current: false })
      .eq('id', parentRecipe.id)

    if (updateParentError) {
      // Attempt rollback: delete newly created recipe
      await supabase.from('substrate_recipes').delete().eq('id', newMainRecipe.id)
      throw new Error(`Failed to update parent recipe: ${updateParentError.message}`)
    }

    // 5. Copy ingredients from experimental to new main recipe
    if (experimental.recipe_ingredients && experimental.recipe_ingredients.length > 0) {
      const newIngredients = experimental.recipe_ingredients.map(ing => ({
        recipe_id: newMainRecipe.id,
        material_name: ing.material_name,
        parts: ing.parts ?? ing.quantity_kg,
        quantity_kg: ing.parts ?? ing.quantity_kg,
        notes: ing.notes,
        item_id: ing.item_id,
        track_inventory: ing.track_inventory
      }))

      const { error: ingredientsError } = await supabase
        .from('recipe_ingredients')
        .insert(newIngredients)

      if (ingredientsError) {
        // Attempt rollback: delete newly created recipe and restore parent
        await supabase.from('substrate_recipes').delete().eq('id', newMainRecipe.id)
        await supabase.from('substrate_recipes').update({ is_current: true }).eq('id', parentRecipe.id)
        throw new Error(`Failed to copy ingredients: ${ingredientsError.message}`)
      }
    }

    // 6. Fetch and return the complete new main recipe with ingredients
    const { data: completeRecipe, error: finalFetchError } = await supabase
      .from('substrate_recipes')
      .select(`
        id,
        name,
        compatible_species,
        mixer_capacity_kg,
        blocks_per_batch,
        version_major,
        version_minor,
        parent_recipe_id,
        is_current,
        active,
        notes,
        created_at,
        updated_at,
        recipe_ingredients (
          id,
          material_name,
          parts,
          quantity_kg,
          notes,
          item_id,
          track_inventory
        )
      `)
      .eq('id', newMainRecipe.id)
      .single()

    if (finalFetchError) {
      throw new Error(`Promotion succeeded but failed to fetch complete recipe: ${finalFetchError.message}`)
    }

    return {
      ...completeRecipe,
      ingredients: completeRecipe.recipe_ingredients || []
    }
  } catch (err) {
    console.error('[promoteToMain] Error:', err)
    throw err
  }
}
